import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/** Original static instruction for fourteen exact lower-body variants.
 * Sources and declared apparatus adaptations are recorded in
 * docs/research/fitness-rea-20261015/guidance-drafts/lower14/sources.json.
 * No camera observation, personalized assessment or professional review is implied.
 */
export const LOWER_EXERCISE_GUIDANCE_IDS = [
  "plan-front-squat",
  "plan-pause-squat",
  "free-glutes-sumo",
  "free-legs-bulgaro",
  "free-legs-stiff",
  "free-glutes-bom-dia",
  "free-glutes-hip-thrust",
  "free-glutes-glute-bridge",
  "free-glutes-stiff-unilateral",
  "seated-hip-abduction",
  "free-legs-cadeira-extensora",
  "free-legs-panturrilha-sentado",
  "plan-deficit-deadlift",
  "plan-box-jump"
] as const;
export type LowerExerciseGuidanceId = typeof LOWER_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type LowerExerciseGuidanceDictionary = Record<LowerExerciseGuidanceId, GuidanceCopy>;
const supported = new Set<string>(LOWER_EXERCISE_GUIDANCE_IDS);

/** Exact app identity/canonical name; provider IDs and translated displays are not inferred. */
export function getLowerExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  if (!id || !supported.has(id)) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'lower', id);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
